import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
} from '@material-ui/core';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useStyles } from 'components/FilterDropdown/styles';
import { Filterable } from 'components/FilterDropdown/util';
import SearchInput from 'components/SearchInput';
import globalMessages from 'shared/global/messages';

import { messages } from '../messages';
import { SupportedFilterableTypes } from '../types';

export interface FilterOpenProps<T> {
  name: SupportedFilterableTypes;
  setQuery: (query: string) => void;
  selected: T[];
  setSelected: (selection: T[]) => void;
  options: T[];
  fetchNextPage?: () => void;
  isLoading?: boolean;
}

export const FilterOpen: FC<FilterOpenProps<Filterable>> = ({
  name,
  selected,
  setSelected,
  setQuery,
  options,
  fetchNextPage,
  isLoading = false,
}) => {
  const [search, setSearch] = useState('');
  const classes = useStyles();
  const intl = useIntl();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const remaining = options.filter(
    opening => !selected.some(option => option.id === opening.id),
  );

  useEffect(() => {
    setQuery(search);
  }, [search, setQuery]);

  useEffect(() => {
    const container = scrollContainerRef.current;

    const handleScroll = () => {
      if (!container || !fetchNextPage) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      if (scrollPercentage >= 0.8) {
        fetchNextPage();
      }
    };

    container?.addEventListener('scroll', handleScroll);

    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, [fetchNextPage]);

  return (
    <Paper
      ref={scrollContainerRef}
      variant="outlined"
      className={
        classes[name === 'stage' ? 'rightOpenContainer' : 'openContainer']
      }
      style={{ maxHeight: '300px', overflowY: 'auto' }}
    >
      <SearchInput
        // @ts-expect-error this will go away when `SearchInput` is converted to typescript
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearch(e.target.value)
        }
        value={search}
        fullWidth
        className={classes.searchInput}
        onClear={() => setSearch('')}
        placeholder={intl.formatMessage(messages.searchPlaceholder, { name })}
      />
      {selected.map(option => (
        <Grid key={option.id}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked
                onChange={() =>
                  setSelected(
                    selected.filter(opening => opening.id !== option.id),
                  )
                }
              />
            }
            label={option.title}
            className={classes.checks}
          />
        </Grid>
      ))}
      {selected.length > 0 && remaining.length > 0 && (
        <Grid className={classes.separator} />
      )}
      {remaining.map(option => (
        <Grid key={option.id}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={false}
                onChange={() => setSelected([...selected, option])}
              />
            }
            label={option.title}
            title={option.title}
            className={classes.checks}
          />
        </Grid>
      ))}
      {isLoading && (
        <div className={classes.loadingRow} role="status">
          <CircularProgress size={16} />
          <FormattedMessage {...globalMessages.loading} />
        </div>
      )}
    </Paper>
  );
};
